import { getFromLocalStorage } from './storage';
import {
  CHAIN_URLS,
  getInitialProductBalancesColumns,
  getInitialCampaignsListColumns,
  getInitialCampaignProductsListColumns,
} from './constants';
import moment from 'moment';
import type { UserData } from '@/server/user';
import type { AnyAppData } from '@/server/app-data';
import type { SalesOrdersShop } from '@/server/sales-orders/sales-orders-shops.js';
import type {
  AdditionalRichData,
  AdditionalShopData,
  SalesOrder,
} from '@/server/sales-orders/sales-orders-get';
import type { SalesOrdersDeliveryTrackingFilters } from '@/lib/redux/redux-actions/sales-orders-delivery-tracking';
import type { SalesOrderDeliveryTrackingItem } from '@/server/sales-orders/sales-order-delivery-tracking';
import type { AssortmentRich, Assortment } from '@/server/assortments/assortments';
import type { FormattedPricatProductColor } from '@/server/pricat/pricat-products';
import type {
  ProductCreationDraft,
  ProductVariationData,
} from '../redux/redux-actions/b24-products';
import type { AssortmentProductEvent } from '@/server/events/events';
import type { SalesOrderSummaryAmounts } from '@/server/sales-orders/sales-order-summary';
import type { HowToContentResult, HowToContentService } from '@/server/api/routers/stage-content';
import { getValidChainOrDefault } from './chain';
import { type ShopSize } from '@/server/nos/types-maps';
import { getChainFromCookie } from './chain-slug';

// Extend the global Window interface
declare global {
  interface Window {
    __INITIAL_USER_STATE__?: UserData;
  }
}

export const INITAL_TABLE_CONFIG = {
  order: 'asc',
  orderBy: 'pid',
  page: 0,
  rowsPerPage: 100,
  scrollX: 0,
  scrollY: 0,
  selected: [],
  selectedMapped: {},
};

const activeFilters = getFromLocalStorage('activeFilters');
const activeTabs = getFromLocalStorage('activeTabs');
const activeTabMapping = getFromLocalStorage('activeTabsMapping');
const activeSearch = getFromLocalStorage('activeSearch');
const activeOperator = getFromLocalStorage('activeOperator');
const tableConfigs = getFromLocalStorage('tableConfigs');
const productBalancesColumns = getFromLocalStorage('productBalancesColumns');
const campaignsListColumns = getFromLocalStorage('campaignsListColumns');
const campaignProductsListColumns = getFromLocalStorage('campaignProductsListColumns');
const overrideSearch = getFromLocalStorage('overrideSearch');
const activeChainLocal = getFromLocalStorage('activeChain');
const activeService = getFromLocalStorage('activeService');
const activeEnv = getFromLocalStorage('activeEnv');
const assortmentListActiveFilters =
  getFromLocalStorage(`assortmentListActiveFilters-${activeChainLocal}`) ||
  getFromLocalStorage(`assortmentListActiveFilters`); // legacy backwards compatibility
const salesOrderListActiveFilters = getFromLocalStorage('salesOrderListActiveFilters');
const salesOrdersReportActiveFilters = getFromLocalStorage('salesOrdersReportActiveFilters');
const salesOrdersDeliveryTrackingActiveFilters = getFromLocalStorage(
  'salesOrdersDeliveryTrackingActiveFilters'
);
const salesOrdersAdminActiveFilters = getFromLocalStorage('salesOrdersAdminActiveFilters');
const salesOrderListActiveShop = getFromLocalStorage('salesOrderListActiveShop');
const activeProductListColumnPreset = getFromLocalStorage('activeProductListColumnPreset');
const activeProductListColumns = getFromLocalStorage('activeProductListColumns');
const imageSearchActiveFilters = getFromLocalStorage('imageSearchActiveFilters');

const getUserFromGlobal = (): NonNullable<UserData> => {
  if (typeof window !== 'undefined' && window.__INITIAL_USER_STATE__) {
    return window.__INITIAL_USER_STATE__;
  }
  // This should not matter, as we should always have user state when dom is ready
  // TODO: Actually this is bad, and we need to fix it in the future by making the user state is required in protected routes.
  // Casting as object hides the problem until we need to use child props of UserData
  return {} as NonNullable<UserData>;
};

const USER = getUserFromGlobal();

const activeChain = getValidChainOrDefault(
  getChainFromCookie() ?? activeChainLocal ?? undefined,
  USER
);

const ASSORTMENT: {
  isLoading: boolean;
  error: string | null;
  assortment: AssortmentRich;
  events: AssortmentProductEvent[];
} = {
  isLoading: false,
  error: null,
  assortment: {} as AssortmentRich,
  events: [],
};

const ASSORTMENTS_SEARCH = {
  activeFilters: {
    type: 'NORMAL',
    brand: '',
    season: '',
    generalItemCategoryGroup: '',
    quarter: '',
    productType: '',
    assortmentLead: null,
    nosType: '',
    status: '',
    startDate: null,
    endDate: null,
    size: '',
  },
  canLoadMore: false,
};

const ASSORTMENTS: {
  isLoading: boolean;
  error: string | null;
  assortments: Assortment[];
} = {
  isLoading: false,
  error: null,
  assortments: [],
};

const B24_PRODUCTS_SEARCH = {
  activeFilters: {
    brand: '',
    productType: '',
    productCode: '',
    season: '',
  },
  canLoadMore: false,
};

const B24_PRODUCTS = {
  isLoading: false,
  error: null,
  products: [] as FormattedPricatProductColor[],
  creationQueue: {
    productsMappedByProductCode: {} as Record<string, ProductCreationDraft>,
    variantsMappedByProductCode: {} as Record<string, ProductVariationData[]>,
    selectedIds: [] as string[],
  },
};

export type HowToContentState = {
  contentMapped: HowToContentResult | null;
  isLoading: boolean;
  error: string | null;
  service: HowToContentService | null;
};
const HOWTO_CONTENT: HowToContentState = {
  contentMapped: null,
  isLoading: false,
  error: null,
  service: null,
};

const IMAGE_SEARCH = {
  isLoading: false,
  error: null,
  images: [],
  activeOperator: 'AND',
  activeFilters: {
    brand: '',
    targetGroup: '',
    season: '',
    sport: '',
    imgType: '',
    imgWidth: '',
    imgHeight: '',
    sapId: '',
    kwNumber: '',
    productName: '',
  },
};

const QUICK_SEARCH = {
  isLoading: false,
  error: null,
  searchResult: null,
};

const PRODUCT_PAGE_DATA: {
  isLoading: boolean;
  error: string | null;
  productPageData: AnyAppData;
} = {
  isLoading: false,
  error: null,
  productPageData: {},
};

const PRODUCT = {
  isLoading: false,
  error: null,
  product: {},
  balancesTableColumns: [],
};

const PRODUCT_MEDIA = {
  productMedia: {},
};

const PRODUCTS_SEARCH = {
  isLoading: false,
  error: null,
  activeFilters: {},
  activeSearch: null,
  activeOperator: 'AND',
  overrideSearch: false,
  savedSearches: null,
};

const PRODUCTS_TAB_MENU = {
  activeTabs: [],
  activeTabMapping: {},
};

const PRODUCTS = {
  isLoading: false,
  error: null,
  products: [],
  lastVisitedProduct: null,
};

const PRODUCT_LIST_COLUMNS = {
  isLoading: false,
  error: null,
  activeColumns: [
    { id: 'sapId' },
    { id: 'images' },
    { id: 'brand' },
    { id: 'sapName' },
    { id: 'kwNumber' },
    { id: 'price' },
    { id: 'description' },
    { id: 'season' },
    { id: 'targetgroup' },
    { id: 'inCampaign' },
  ],
  activePreset: null,
  //overridePreset: false, DO WE NEED THIS?
  savedPresets: null,
};
export type SalesOrderSendItem = {
  endpointParams: {
    salesOrderGroupId: number;
    shopId: number;
    assortmentProductColorId: number;
    componentId?: number;
    b24ProductVariantId?: number;
  };
  endpointParamsString: string;
  items: Record<string, { deliveryDate: string; amount: number; hasError?: boolean }>;
};
export type SalesOrderSendConfig = {
  items: Record<
    string,
    Record<
      ShopSize,
      {
        storeRank: ShopSize;
        deliveryDate: string;
        amount: number;
        hasError?: boolean;
      }
    >
  >;
  endpointParams: {
    salesOrderGroupId: number;
    shopId: number;
    componentId?: number;
    b24ProductVariantId: number;
    assortmentProductColorId: number;
  };
};

export type SalesOrderAmount = {
  deliveryDate: string;
  amount: number;
  hasError?: boolean;
};

export type SalesOrderDeliveryDateAmounts = Record<string, SalesOrderAmount>;
export type SalesOrderItemAmounts = Record<string, SalesOrderDeliveryDateAmounts>;
export type SalesOrderProductAmounts = Record<string, SalesOrderItemAmounts>;

// Types for the amounts structure
export type SalesOrderAmounts = Record<
  number, // assortmentProductId
  SalesOrderProductAmounts
>;
const SALES_ORDER = {
  isSaving: false,
  isLoading: false,
  error: null,
  response: null,
  salesOrder: {} as SalesOrder & AdditionalRichData & AdditionalShopData,
  shopsOrders: [],
  summaryAmounts: {} as SalesOrderSummaryAmounts,
  sendConfigs: [] as SalesOrderSendConfig[],
  sendItems: [] as SalesOrderSendItem[],
  pendingItems: false,
  errorPopup: null as null | {
    title: string;
    message: string;
    action: () => void;
    confirmText: string;
    showCancel: boolean;
  },
  inputErrors: [] as string[],
  configInputErrors: [] as string[],
  orderReport: {
    products: [],
    orderCount: 0,
    productCount: 0,
    totalAmount: 0,
    totalSum: 0,
  },
  reviewReportData: null,
  reportDialogOpen: false,
};

const SALES_ORDERS = {
  isLoading: false,
  error: null,
  salesOrders: [],
  activeShop: {},
  reportData: null,
  reportDialogOpen: false,
};

const SALES_ORDER_AMOUNTS = {
  amounts: {} as SalesOrderAmounts,
};

const SALES_ORDERS_SEARCH = {
  activeShopId: null,
  activeFilters: {
    season: '',
    quarter: '',
    generalItemCategoryGroup: '',
    brand: '',
    type: '',
    status: '',
  },
  activeTab: 'SALES_ORDERS_ACTIVE',
};

const SALES_ORDERS_REPORT_SEARCH = {
  activeFilters: {
    season: '',
    campaign: '',
    onlyUnorderedProducts: false,
    campaignLevelRoles: [],
    brands: ['ADIDAS'],
    productType: '',
    targetGroups: [],
    category: '',
    sport: '',
    roles: [],
    status: '',
    shopId: '',
  },
};
const SALES_ORDERS_DELIVERY_TRACKING = {
  activeFilters: {
    season: '',
    quarters: [],
    campaign: '',
    brands: [],
    productType: '',
    onlyChangedProducts: false,
    shopId: undefined as number | undefined,
  } satisfies SalesOrdersDeliveryTrackingFilters,
  items: [] as SalesOrderDeliveryTrackingItem[],
};

const SALES_ORDERS_ADMIN = {
  isLoading: false,
  activeFilters: {
    seasons: [],
    brands: [],
    quarters: [],
    groups: [],
    types: [],
    status: [],
    deadline: '',
  },
  orders: [],
};

const SALES_ORDERS_SHOPS: {
  isLoading: boolean;
  error: string | null;
  shops: SalesOrdersShop[];
  shop: SalesOrdersShop;
} = {
  isLoading: false,
  error: null,
  shops: [],
  shop: {} as SalesOrdersShop,
};

const SALES_ORDER_B24_STATUS = {
  isLoading: false,
  error: null,
  activeFilters: {
    changedOnly: false,
  },
  items: [],
};

const SALES_ORDERS_NEW_COLOR_COMPONENTS = {
  message: false,
};

const SALES_ORDERS_NEW_DEFAULT_COMPONENTS = {
  isLoading: false,
  errorMessage: false,
  dialogOpen: false,
  inputErrors: [],
  items: [
    {
      id: 1,
      size: '',
      amount: '',
    },
  ],
};

const SHOP_DATA = {
  isLoading: false,
  error: null,
  shopData: {},
};

const TABLE_COLUMNS = {
  productBalancesColumns: {
    active: getInitialProductBalancesColumns(activeChain),
    all: {},
  },
  campaignsListColumns: {
    active: getInitialCampaignsListColumns(),
    all: {},
  },
  campaignProductsListColumns: {
    active: getInitialCampaignProductsListColumns(),
    all: {},
  },
};

const TABLE_CONFIGS = {
  productsListConfig: tableConfigs?.productsListConfig || INITAL_TABLE_CONFIG,
  supplementOrdersProductListConfig:
    tableConfigs?.supplementOrdersProductListConfig || INITAL_TABLE_CONFIG,
};

const DELIVERY_DATES_DIALOG = {
  dialogs: {} as Record<string, boolean>,
};

const SYSTEM = {
  isLoading: false,
  error: null,
  systemInfo: {
    maintenanceBreaks: [],
    maintenanceBreaksMap: {},
  },
};

export type SystemState = typeof SYSTEM;

const USER_STATE = {
  ...USER,
  activeChain: activeChain,
  chainUrls: CHAIN_URLS[activeChain]?.urls || {},
  activeService: (activeService as string) || '',
};

export type UserState = typeof USER_STATE;

export const INITIAL_STATE = {
  b24ProductsSearch: B24_PRODUCTS_SEARCH,
  b24Products: B24_PRODUCTS,
  howtoContent: HOWTO_CONTENT,
  imageSearch: {
    ...IMAGE_SEARCH,
    activeFilters: {
      ...IMAGE_SEARCH.activeFilters,
      ...(imageSearchActiveFilters || {}),
    },
  },
  productPageData: PRODUCT_PAGE_DATA,
  product: PRODUCT,
  productsSearch: {
    ...PRODUCTS_SEARCH,
    activeFilters: activeFilters || {},
    activeSearch: activeSearch || null,
    activeOperator: activeOperator || 'AND',
    overrideSearch: overrideSearch || false,
  },
  productsTabMenu: {
    ...PRODUCTS_TAB_MENU,
    activeTabs: activeTabs || [],
    activeTabMapping: activeTabMapping || {},
  },
  products: PRODUCTS,
  productListColumns: {
    ...PRODUCT_LIST_COLUMNS,
    activeColumns: activeProductListColumns || PRODUCT_LIST_COLUMNS.activeColumns,
    activePreset: activeProductListColumnPreset || null,
  },
  quickSearch: QUICK_SEARCH,
  assortment: ASSORTMENT,
  assortmentsSearch: {
    ...ASSORTMENTS_SEARCH,
    activeFilters: {
      ...ASSORTMENTS_SEARCH.activeFilters,
      ...(assortmentListActiveFilters || {}),
    },
  },
  assortments: ASSORTMENTS,
  salesOrder: SALES_ORDER,
  salesOrderAmounts: SALES_ORDER_AMOUNTS,
  salesOrdersSearch: {
    ...SALES_ORDERS_SEARCH,
    activeShopId: salesOrderListActiveShop || SALES_ORDERS_SEARCH.activeShopId,
    activeFilters: {
      ...SALES_ORDERS_SEARCH.activeFilters,
      ...(salesOrderListActiveFilters || {}),
    },
  },
  salesOrdersReportSearch: {
    ...SALES_ORDERS_REPORT_SEARCH,
    activeFilters: {
      ...SALES_ORDERS_REPORT_SEARCH.activeFilters,
      ...(salesOrdersReportActiveFilters || {}),
    },
  },
  salesOrdersDeliveryTracking: {
    ...SALES_ORDERS_DELIVERY_TRACKING,
    activeFilters: {
      ...SALES_ORDERS_DELIVERY_TRACKING.activeFilters,
      ...(salesOrdersDeliveryTrackingActiveFilters || {}),
    },
  },
  salesOrdersAdmin: {
    ...SALES_ORDERS_ADMIN,
    activeFilters: {
      ...SALES_ORDERS_ADMIN.activeFilters,
      ...(salesOrdersAdminActiveFilters || {}),
    },
  },
  salesOrders: SALES_ORDERS,
  salesOrdersShops: SALES_ORDERS_SHOPS,
  salesOrderB24Status: SALES_ORDER_B24_STATUS,
  salesOrdersNewColorComponents: SALES_ORDERS_NEW_COLOR_COMPONENTS,
  salesOrdersNewDefaultComponents: SALES_ORDERS_NEW_DEFAULT_COMPONENTS,
  shopData: SHOP_DATA,
  system: SYSTEM,
  tableColumns: {
    ...TABLE_COLUMNS,
    productBalancesColumns: {
      active: productBalancesColumns || getInitialProductBalancesColumns(activeChain),
      all: {},
    },
    campaignsListColumns: {
      active: campaignsListColumns || getInitialCampaignsListColumns(),
      all: {},
    },
    campaignProductsListColumns: {
      active: campaignProductsListColumns || getInitialCampaignProductsListColumns(),
      all: {},
    },
  },
  tableConfigs: TABLE_CONFIGS,
  deliveryDatesDialog: DELIVERY_DATES_DIALOG,
  user: USER_STATE,
};

const AUTH = {
  error: false,
  chainError: false,
};

export const REDUCER_STATES = {
  b24ProductsSearch: B24_PRODUCTS_SEARCH,
  b24Products: B24_PRODUCTS,
  howtoContent: HOWTO_CONTENT,
  imageSearch: IMAGE_SEARCH,
  productPageData: PRODUCT_PAGE_DATA,
  product: PRODUCT,
  productsSearch: PRODUCTS_SEARCH,
  productsTabMenu: PRODUCTS_TAB_MENU,
  products: PRODUCTS,
  productListColumns: PRODUCT_LIST_COLUMNS,
  quickSearch: QUICK_SEARCH,
  assortment: ASSORTMENT,
  assortmentsSearch: ASSORTMENTS_SEARCH,
  assortments: ASSORTMENTS,
  salesOrder: SALES_ORDER,
  salesOrders: SALES_ORDERS,
  salesOrderAmounts: SALES_ORDER_AMOUNTS,
  salesOrdersSearch: SALES_ORDERS_SEARCH,
  salesOrdersReportSearch: SALES_ORDERS_REPORT_SEARCH,
  salesOrdersDeliveryTracking: SALES_ORDERS_DELIVERY_TRACKING,
  salesOrdersAdmin: SALES_ORDERS_ADMIN,
  salesOrdersShops: SALES_ORDERS_SHOPS,
  salesOrderB24Status: SALES_ORDER_B24_STATUS,
  salesOrdersNewColorComponents: SALES_ORDERS_NEW_COLOR_COMPONENTS,
  salesOrdersNewDefaultComponents: SALES_ORDERS_NEW_DEFAULT_COMPONENTS,
  shopData: SHOP_DATA,
  system: SYSTEM,
  tableColumns: TABLE_COLUMNS,
  tableConfigs: TABLE_CONFIGS,
  deliveryDatesDialog: DELIVERY_DATES_DIALOG,
  user: USER_STATE,
  auth: AUTH,
};
